/* eslint-disable max-len */
import React, { useRef, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { IoIosAdd } from 'react-icons/io';
import {
  HiOutlinePencil,
  HiCheck,
  HiX,
} from 'react-icons/hi';
import { updateCase, deleteFolderCase, updateFolderCase } from '../../actions/case';
import { uploadFile } from '../../actions/file';
import { getData } from '../../actions';
import { hasAccess, downloadDocument, downloadTransportEvent } from '../../utils';
import Loader from '../../components/Loader';
import AddPictureToDocumentModal from '../../components/AddPictureToDocumentModal';
import imgCamera from '../../assets/images/icons/camera.svg';
import fileIcon from '../../assets/images/icons/file-grey.svg';
import iconTransport from '../../assets/images/icons/transport.svg';
import FoldersList from '../../components/list/FoldersList';
import {
  Textarea,
  InputText,
  ErrorField,
} from '../../lib/HooksFormFields';
import styles from './case.module.scss';

const RootCase = ({
  selectedCase,
  currentFolder,
  searchParams,
  modalCreateFolder,
  setSendDocuments,
  openFolder,
  openDocumentFolder,
}) => {
  const dispatch = useDispatch();
  const { authReducer, caseReducer } = useSelector((store) => store);
  const { user } = authReducer;
  const { error: caseError } = caseReducer;
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [sageDocuments, setSageDocuments] = useState([]);
  const [loadingDocumentNumber, setLoadingDocumentNumber] = useState(null);
  const addPictureModalRef = useRef();
  const [addPictureDocumentNumber, setAddPictureDocumentNumber] = useState(null);
  const {
    control,
    handleSubmit,
    watch,
  } = useForm({
    defaultValues: {
      searchNumber: '',
    },
  });

  const searchNumber = watch('searchNumber');

  const getBaseTypeForTaskType = (taskType) => {
    if (taskType === 'HS') return 'hs';
    if (taskType === 'Préparation' || taskType === 'Chargement') return 'departure';
    if (taskType === 'Déchargement' || taskType === 'Retour') return 'return';
    return 'return';
  };

  const normalizeExistingPicture = (p) => {
    const fileId = p?.file?._id || p?.file || p?._id;
    if (!fileId) return null;
    const baseType = p?.type || getBaseTypeForTaskType(p?.taskType);
    const payload = { file: fileId, type: baseType };
    if (p?.number) payload.number = p.number;
    if (baseType !== 'hs' && p?.taskType) payload.taskType = p.taskType;
    return payload;
  };

  const openAddPictureModal = (documentNumber, piecePL) => {
    const rawDocumentNumber = piecePL || documentNumber;
    const normalized = rawDocumentNumber && rawDocumentNumber !== 'Sans numéro'
      ? rawDocumentNumber
      : null;
    setAddPictureDocumentNumber(normalized);
    addPictureModalRef.current?.open?.();
  };

  const handleAddPictureToDocument = async ({ file, taskType }) => {
    if (!selectedCase?._id || !file || !taskType) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name);
    const res = await uploadFile(formData);
    if (res?.status !== 201) return;

    const baseType = getBaseTypeForTaskType(taskType);
    const newPictures = (res.data.files || []).map((f) => {
      const fileId = f?._id || f?.file?._id || f?.file;
      const payload = { file: fileId, type: baseType };
      if (addPictureDocumentNumber) payload.number = addPictureDocumentNumber;
      if (baseType !== 'hs') payload.taskType = taskType;
      return payload;
    }).filter((p) => p?.file);

    const existingPictures = (selectedCase.pictures || [])
      .map(normalizeExistingPicture)
      .filter(Boolean);

    await updateCase(dispatch, {
      _id: selectedCase._id,
      pictures: [...newPictures, ...existingPictures],
    });
  };

  const handleDownloadDocument = async (number) => {
    if (loadingDocumentNumber) return;
    setLoadingDocumentNumber(number);
    try {
      await downloadDocument(number, number);
    } finally {
      setLoadingDocumentNumber(null);
    }
  };

  // Fonction pour formater la date en dd/mm/yyyy
  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Fonction pour vérifier si le PDF peut être généré (même logique que UpdateTransportEvent)
  const canGeneratePDF = (transport) => {
    if (!transport) return false;
    return !!(
      transport.type
      && transport.buyer
      && transport.carrier
      && typeof transport.quantity === 'number'
      && typeof transport.price === 'number' && transport.price !== 0
      && transport.trunkType
      && transport.loadDate
      && transport.deliveryDate
      && transport.purchaseOrder && /^AC[a-zA-Z-0-9]{6,8}$/.test(transport.purchaseOrder)
      && (
        (
          transport.direction === 'departure'
          && transport.deliveryLocation?.address
        ) || (
          transport.direction === 'return'
          && transport.loadLocation?.address
        )
      )
    );
  };

  // Fonction pour trouver le transport correspondant à un numéro
  const findTransportByNumber = (number) => {
    if (!selectedCase?.transports || !number) return null;
    return selectedCase.transports.find((transport) => transport.number === number);
  };

  // Normaliser un numéro pour comparaison (LPS95579 vs LPS 95579, éviter doublons)
  const normalizeNumber = (num) => {
    if (num == null || num === '' || num === 'Sans numéro') return String(num ?? '');
    return String(num).trim().replace(/\s+/g, '').replace(/-/g, '');
  };

  // Un seul élément par file._id (aligné avec PictureCase)
  const uniquePicturesByFileId = (pictures) => {
    const seen = new Set();
    return (pictures || []).filter((p) => {
      const id = p.file?._id;
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  // Récupérer les documents depuis l'API Sage
  useEffect(() => {
    const fetchSageDocuments = async () => {
      if (!selectedCase?._id) return;

      try {
        const url = `/sage/case/${selectedCase._id}/documents`;
        const response = await getData(null, url, null, true);

        if (response?.data?.documents) {
          // Trier par date (plus récente d'abord)
          const sortedDocuments = [...response.data.documents].sort((a, b) => {
            const dateA = a.date ? new Date(a.date) : new Date(0);
            const dateB = b.date ? new Date(b.date) : new Date(0);
            return dateB - dateA;
          });
          setSageDocuments(sortedDocuments);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des documents Sage:', error);
      }
    };

    fetchSageDocuments();
  }, [selectedCase?._id]);

  function handleSaveComment(data) {
    updateCase(dispatch, {
      ...selectedCase,
      comment: data.comment,
    }).then(() => {
      setIsEditingComment(false);
    });
  }

  return (
    <>
      {/* COMMENTAIRES */}
      {!searchParams.get('pictures') && !searchParams.get('number') && currentFolder?.root && (
        <div className={styles.comment}>
          <div className={styles.commentHeader}>
            <h3>Commentaire</h3>
            {!isEditingComment && (
              <button
                className={styles.editButton}
                onClick={() => setIsEditingComment(true)}
                title="Modifier le commentaire"
              >
                <HiOutlinePencil size="16px" />
              </button>
            )}
          </div>

          {isEditingComment ? (
            <form onSubmit={handleSubmit(handleSaveComment)}>
              <Textarea
                name="comment"
                control={control}
                defaultValue={selectedCase.comment || ''}
                placeholder="Ajouter un commentaire..."
                label=""
              />
              <div className={styles.commentActions}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={() => setIsEditingComment(false)}
                >
                  <HiX size="16px" />
                  Annuler
                </button>
                <button
                  type="submit"
                  className={styles.saveButton}
                >
                  <HiCheck size="16px" />
                  Sauvegarder
                </button>
              </div>
            </form>
          ) : (
            <p>{selectedCase.comment || 'Aucun commentaire'}</p>
          )}
        </div>
      )}

      {/* FOLDERS List */}
      {!searchParams.get('pictures') && !searchParams.get('number') && currentFolder?.root && (
        <div className={styles.folders}>
          <div className={styles.header}>
            <h3></h3>
            {hasAccess(user.role) && (
              <button onClick={() => modalCreateFolder.current.open()}>
                <span className='icon'><IoIosAdd size='20px' /></span>
                <span>Créer un dossier</span>
              </button>
            )}
          </div>
          {caseError && (
            <div className={styles.errorMessage}>
              <ErrorField
                message={caseError === 'Folder is not empty.'
                  ? 'Impossible de supprimer le dossier : il n\'est pas vide.'
                  : caseError}
              />
            </div>
          )}
          <FoldersList
            folders={currentFolder?.folders}
            handleClickFolder={openFolder}
            actions={{
              print: true,
              rename: updateFolderCase,
              send: (doc) => {
                setSendDocuments(doc);
              },
              delete: (folder) => deleteFolderCase(dispatch, folder),
            }}
          />
        </div>
      )}

      {/* DOCUMENTS */}
      {!searchParams.get('pictures') && !searchParams.get('number') && currentFolder?.root && (
        <>
          <div className={styles.documentsHeader}>
            <h3>Documents</h3>
            <div style={{ flex: 1, maxWidth: '250px', marginLeft: '20px' }}>
              <InputText
                name="searchNumber"
                control={control}
                placeholder="Rechercher un départ ou un retour..."
                label=""
              />
            </div>
          </div>
          <div className={styles.documents}>
            <div className={styles.tiles}>
              <div className={styles.header}>
                <h3>Départs</h3>
              </div>
              {(() => {
                // Récupérer les numéros de type "Chargement" ou "Préparation"
                const tasks = selectedCase?.tasks
                  ? selectedCase.tasks.filter((task) => task.type === 'Chargement' || task.type === 'Préparation')
                  : [];

                // Récupérer les transports avec direction "departure"
                const departureTransports = selectedCase?.transports
                  ? selectedCase.transports.filter((transport) => transport.direction === 'departure')
                  : [];

                const tasksWithNumber = tasks.filter((task) => task.number);
                const tasksWithoutNumber = tasks.filter((task) => !task.number);
                const transportsWithNumber = departureTransports.filter((transport) => transport.number);

                const taskNumbers = [...new Set(tasksWithNumber.map((task) => task.number))];
                const transportNumbers = [...new Set(transportsWithNumber.map((transport) => transport.number))];

                // Combiner les numéros de tâches et de transports
                const allNumbers = [...new Set([...taskNumbers, ...transportNumbers])];

                // Ajouter "Sans numéro" si des tâches sans numéro ou des pictures départ sans numéro existent
                const hasDeparturePicturesWithoutNumber = (currentFolder.pictures || []).some((f) => (
                  f.type === 'departure'
                  && (!f.number || f.number === null || f.number === undefined)
                ));
                if (tasksWithoutNumber.length > 0 || (hasDeparturePicturesWithoutNumber && !allNumbers.includes('Sans numéro'))) {
                  allNumbers.push('Sans numéro');
                }

                // Récupérer les documents Sage pour les départs (LPS, LPT, LL) avec leur date
                const sageDepartDocuments = sageDocuments
                  .filter((doc) => {
                    const piece = doc.piece || '';
                    return piece.includes('LPS') || piece.includes('LPT') || piece.includes('LL');
                  })
                  .map((doc) => ({
                    number: doc.piece,
                    piecePL: doc.piecePL || null,
                    date: doc.date ? new Date(doc.date) : null,
                    year: doc.date ? new Date(doc.date).getFullYear() : null,
                  }));

                // Créer une structure avec année pour l'affichage
                const itemsWithYear = [];
                const addedSageNormalized = new Set();

                // Ajouter les documents Sage (un seul par numéro normalisé pour éviter doublons)
                sageDepartDocuments.forEach((doc) => {
                  const key = normalizeNumber(doc.number);
                  if (addedSageNormalized.has(key)) return;
                  addedSageNormalized.add(key);
                  itemsWithYear.push({ type: 'document', ...doc });
                });

                // Numéros déjà couverts par les documents Sage (piece + piecePL, normalisés pour éviter LPS95579 vs LPS 95579)
                const sageNumbers = new Set();
                sageDepartDocuments.forEach((d) => {
                  sageNumbers.add(normalizeNumber(d.number));
                  if (d.piecePL) sageNumbers.add(normalizeNumber(d.piecePL));
                });
                allNumbers.forEach((number) => {
                  if (!sageNumbers.has(normalizeNumber(number))) {
                    // Chercher d'abord dans les tâches
                    const matchingTask = tasks.find((task) => {
                      if (number === 'Sans numéro') {
                        return !task.number;
                      }
                      return task.number === number;
                    });

                    // Si pas trouvé dans les tâches, chercher dans les transports
                    let matchingTransport = null;
                    if (!matchingTask) {
                      matchingTransport = departureTransports.find((transport) => transport.number === number);
                    }

                    let itemDate = null;
                    if (matchingTask) {
                      itemDate = matchingTask.date ? new Date(matchingTask.date) : null;
                    } else if (matchingTransport) {
                      // Pour les départs, utiliser deliveryDate
                      itemDate = matchingTransport.deliveryDate ? new Date(matchingTransport.deliveryDate) : null;
                    }

                    itemsWithYear.push({
                      type: matchingTransport ? 'transport' : 'task',
                      number,
                      date: itemDate,
                      year: itemDate ? itemDate.getFullYear() : null,
                    });
                  }
                });

                // Trier par année (décroissante) puis par date — années en nombre pour éviter ordre incohérent
                itemsWithYear.sort((a, b) => {
                  const yA = a.year != null ? Number(a.year) : -1;
                  const yB = b.year != null ? Number(b.year) : -1;
                  if (yB !== yA) return yB - yA;
                  const dA = a.date ? (a.date instanceof Date ? a.date : new Date(a.date)).getTime() : 0;
                  const dB = b.date ? (b.date instanceof Date ? b.date : new Date(b.date)).getTime() : 0;
                  return dB - dA;
                });

                // Grouper par année et créer la structure d'affichage
                const groupedItems = [];
                let currentYear = null;

                itemsWithYear.forEach((item) => {
                  const itemYear = item.year != null ? Number(item.year) : null;
                  if (itemYear !== null && itemYear !== currentYear) {
                    groupedItems.push({ type: 'yearSeparator', year: itemYear });
                    currentYear = itemYear;
                  }
                  groupedItems.push(item);
                });

                // Filtrer par numéro de recherche
                const filteredItems = searchNumber
                  ? groupedItems.filter((item) => {
                    if (item.type === 'yearSeparator') return true; // Garder les séparateurs d'année
                    const numberStr = item.number?.toString().toLowerCase() || '';
                    const searchStr = searchNumber.toLowerCase();
                    return numberStr.includes(searchStr);
                  })
                  : groupedItems;

                return filteredItems.length > 0 ? (
                  <div className={styles.taskNumbersList}>
                    {filteredItems.map((item, index) => {
                      if (item.type === 'yearSeparator') {
                        return (
                          <div key={`year-separator-${item.year}-${index}`} className={styles.yearSeparator}>
                            <span>{item.year}</span>
                          </div>
                        );
                      }
                      const departureFiltered = item.number === 'Sans numéro'
                        ? currentFolder.pictures.filter((f) => f.type === 'departure'
                          && (!f.number
                          || f.number === null
                          || f.number === undefined))
                        : currentFolder.pictures.filter((f) => (
                          normalizeNumber(f.number) === normalizeNumber(item.number)
                          || (item.piecePL && normalizeNumber(f.number) === normalizeNumber(item.piecePL))
                        ));
                      const photoCount = uniquePicturesByFileId(departureFiltered).length;

                      const matchingTransport = findTransportByNumber(item.number)
                        || (item.piecePL ? findTransportByNumber(item.piecePL) : null);
                      const canDownloadTransport = canGeneratePDF(matchingTransport);

                      return (
                        <div key={`task-number-${item.number}-${index}`} className={styles.taskNumber}>
                          <div>
                            {item.number}
                            {item.piecePL && (
                              <span className={styles.date}>({item.piecePL})</span>
                            )}
                            {item.date && (
                              <span className={styles.date}>{formatDate(item.date)}</span>
                            )}
                          </div>
                          <div className={styles.iconsContainer}>
                            {photoCount > 0 && (
                              <div className={styles.icon} onClick={() => (
                                openDocumentFolder(item.number, item.piecePL)
                              )}>
                                <img src={imgCamera} alt="photos departs" />
                                <p>{photoCount}</p>
                              </div>
                            )}
                            <button
                              type="button"
                              className={styles.addPictureBtn}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openAddPictureModal(item.number, item.piecePL);
                              }}
                              aria-label="Ajouter une photo"
                            >
                              +
                            </button>
                            {item.type === 'document' && (
                              <div
                                className={styles.icon}
                                onClick={() => handleDownloadDocument(item.number)}
                                style={loadingDocumentNumber === item.number ? { pointerEvents: 'none' } : undefined}
                              >
                                <div className={styles.iconDocumentContent}>
                                  {loadingDocumentNumber === item.number ? (
                                    <Loader size={38} />
                                  ) : (
                                    <img src={fileIcon} alt="file" />
                                  )}
                                </div>
                              </div>
                            )}
                            {canDownloadTransport && matchingTransport && (
                              <div className={styles.icon} onClick={() => (
                                downloadTransportEvent(
                                  matchingTransport._id,
                                  matchingTransport.purchaseOrder || item.number || item.piecePL,
                                )
                              )}>
                                <img src={iconTransport} alt="transport" />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className={styles.noTaskNumbers}>
                    <p>Aucun contenu de départ</p>
                  </div>
                );
              })()}
            </div>
            <div className={styles.tiles}>
              <div className={styles.header}>
                <h3>Retours</h3>
              </div>
              {(() => {
                // Récupérer les numéros de type "Déchargement" ou "Retour"
                const tasks = selectedCase?.tasks
                  ? selectedCase.tasks.filter((task) => task.type === 'Déchargement' || task.type === 'Retour')
                  : [];

                // Récupérer les transports avec direction "return"
                const returnTransports = selectedCase?.transports
                  ? selectedCase.transports.filter((transport) => transport.direction === 'return')
                  : [];

                const tasksWithNumber = tasks.filter((task) => task.number);
                const tasksWithoutNumber = tasks.filter((task) => !task.number);
                const transportsWithNumber = returnTransports.filter((transport) => transport.number);

                const taskNumbers = [...new Set(tasksWithNumber.map((task) => task.number))];
                const transportNumbers = [...new Set(transportsWithNumber.map((transport) => transport.number))];

                // Combiner les numéros de tâches et de transports
                const allNumbers = [...new Set([...taskNumbers, ...transportNumbers])];

                // Ajouter "Sans numéro" si des tâches sans numéro ou des pictures retour sans numéro existent
                const hasReturnPicturesWithoutNumber = (currentFolder.pictures || []).some((f) => (
                  (f.type === 'return' || f.type === 'hs')
                  && (!f.number || f.number === null || f.number === undefined)
                ));
                if (tasksWithoutNumber.length > 0 || (hasReturnPicturesWithoutNumber && !allNumbers.includes('Sans numéro'))) {
                  allNumbers.push('Sans numéro');
                }

                // Récupérer les documents Sage pour les retours (LRS, LRT, RF, RL) avec leur date
                const sageReturnDocuments = sageDocuments
                  .filter((doc) => {
                    const piece = doc.piece || '';
                    return piece.includes('LRS') || piece.includes('LRT') || piece.includes('RF') || piece.includes('RL');
                  })
                  .map((doc) => ({
                    number: doc.piece,
                    piecePL: doc.piecePL || null,
                    date: doc.date ? new Date(doc.date) : null,
                    year: doc.date ? new Date(doc.date).getFullYear() : null,
                  }));

                // Créer une structure avec année pour l'affichage
                const itemsWithYear = [];
                const addedSageNormalizedReturn = new Set();

                // Ajouter les documents Sage (un seul par numéro normalisé pour éviter doublons)
                sageReturnDocuments.forEach((doc) => {
                  const key = normalizeNumber(doc.number);
                  if (addedSageNormalizedReturn.has(key)) return;
                  addedSageNormalizedReturn.add(key);
                  itemsWithYear.push({ type: 'document', ...doc });
                });

                // Numéros déjà couverts par les documents Sage (piece + piecePL, normalisés)
                const sageNumbersReturn = new Set();
                sageReturnDocuments.forEach((d) => {
                  sageNumbersReturn.add(normalizeNumber(d.number));
                  if (d.piecePL) sageNumbersReturn.add(normalizeNumber(d.piecePL));
                });
                allNumbers.forEach((number) => {
                  if (!sageNumbersReturn.has(normalizeNumber(number))) {
                    // Chercher d'abord dans les tâches
                    const matchingTask = tasks.find((task) => {
                      if (number === 'Sans numéro') {
                        return !task.number;
                      }
                      return task.number === number;
                    });

                    // Si pas trouvé dans les tâches, chercher dans les transports
                    let matchingTransport = null;
                    if (!matchingTask) {
                      matchingTransport = returnTransports.find((transport) => transport.number === number);
                    }

                    let itemDate = null;
                    if (matchingTask) {
                      itemDate = matchingTask.date ? new Date(matchingTask.date) : null;
                    } else if (matchingTransport) {
                      // Pour les retours, utiliser loadDate
                      itemDate = matchingTransport.loadDate ? new Date(matchingTransport.loadDate) : null;
                    }

                    itemsWithYear.push({
                      type: matchingTransport ? 'transport' : 'task',
                      number,
                      date: itemDate,
                      year: itemDate ? itemDate.getFullYear() : null,
                    });
                  }
                });

                // Trier par année (décroissante) puis par date — années en nombre pour éviter ordre incohérent
                itemsWithYear.sort((a, b) => {
                  const yA = a.year != null ? Number(a.year) : -1;
                  const yB = b.year != null ? Number(b.year) : -1;
                  if (yB !== yA) return yB - yA;
                  const dA = a.date ? (a.date instanceof Date ? a.date : new Date(a.date)).getTime() : 0;
                  const dB = b.date ? (b.date instanceof Date ? b.date : new Date(b.date)).getTime() : 0;
                  return dB - dA;
                });

                // Grouper par année et créer la structure d'affichage
                const groupedItems = [];
                let currentYear = null;

                itemsWithYear.forEach((item) => {
                  const itemYear = item.year != null ? Number(item.year) : null;
                  if (itemYear !== null && itemYear !== currentYear) {
                    groupedItems.push({ type: 'yearSeparator', year: itemYear });
                    currentYear = itemYear;
                  }
                  groupedItems.push(item);
                });

                // Filtrer par numéro de recherche
                const filteredItems = searchNumber
                  ? groupedItems.filter((item) => {
                    if (item.type === 'yearSeparator') return true; // Garder les séparateurs d'année
                    const numberStr = item.number?.toString().toLowerCase() || '';
                    const searchStr = searchNumber.toLowerCase();
                    return numberStr.includes(searchStr);
                  })
                  : groupedItems;

                return filteredItems.length > 0 ? (
                  <div className={styles.taskNumbersList}>
                    {filteredItems.map((item, index) => {
                      if (item.type === 'yearSeparator') {
                        return (
                          <div key={`year-separator-${item.year}-${index}`} className={styles.yearSeparator}>
                            <span>{item.year}</span>
                          </div>
                        );
                      }
                      const filteredPictures = item.number === 'Sans numéro'
                        ? currentFolder.pictures.filter((f) => (
                          f.type === 'return'
                          || f.type === 'hs'
                        )
                        && (!f.number
                          || f.number === null
                          || f.number === undefined))
                        : currentFolder.pictures.filter((f) => (
                          normalizeNumber(f.number) === normalizeNumber(item.number)
                          || (item.piecePL && normalizeNumber(f.number) === normalizeNumber(item.piecePL))
                        ));
                      const uniquePictures = uniquePicturesByFileId(filteredPictures);
                      const hasHS = uniquePictures.some((f) => f.type === 'hs');
                      const photoCount = uniquePictures.length;

                      const matchingTransport = findTransportByNumber(item.number)
                        || (item.piecePL ? findTransportByNumber(item.piecePL) : null);
                      const canDownloadTransport = canGeneratePDF(matchingTransport);

                      return (
                        <div key={`task-number-${item.number}-${index}`} className={styles.taskNumber}>
                          <div>
                            {item.number}
                            {item.piecePL && (
                              <span> ({item.piecePL})</span>
                            )}
                            {item.date && (
                              <span className={styles.date}>{formatDate(item.date)}</span>
                            )}
                          </div>
                          <div className={styles.iconsContainer}>
                            {photoCount > 0 && (
                              <div className={styles.icon} onClick={() => (
                                openDocumentFolder(item.number, item.piecePL)
                              )}>
                                <img src={imgCamera} alt="photos retour" />
                                <p style={hasHS ? { color: '#F55B5D' } : {}}>
                                  {photoCount}
                                </p>
                              </div>
                            )}
                            <button
                              type="button"
                              className={styles.addPictureBtn}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openAddPictureModal(item.number, item.piecePL);
                              }}
                              aria-label="Ajouter une photo"
                            >
                              +
                            </button>
                            {item.type === 'document' && (
                              <div
                                className={styles.icon}
                                onClick={() => handleDownloadDocument(item.number)}
                                style={loadingDocumentNumber === item.number ? { pointerEvents: 'none' } : undefined}
                              >
                                <div className={styles.iconDocumentContent}>
                                  {loadingDocumentNumber === item.number ? (
                                    <Loader size={38} />
                                  ) : (
                                    <img src={fileIcon} alt="file" />
                                  )}
                                </div>
                              </div>
                            )}
                            {canDownloadTransport && matchingTransport && (
                              <div className={styles.icon} onClick={() => (
                                downloadTransportEvent(
                                  matchingTransport._id,
                                  matchingTransport.purchaseOrder || item.number || item.piecePL,
                                )
                              )}>
                                <img src={iconTransport} alt="transport" />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className={styles.noTaskNumbers}>
                    <p>Aucun contenu de retour</p>
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}
      <AddPictureToDocumentModal
        ref={addPictureModalRef}
        documentNumber={addPictureDocumentNumber}
        onSubmit={handleAddPictureToDocument}
      />
    </>
  );
};

export default RootCase;
